import React, { useState, useEffect } from "react";
import RadioTyped from "./basicInfo/RadioTyped";
import CheckboxTyped from "./basicInfo/CheckboxTyped";
import NumberTyped from "./basicInfo/NumberTyped";
import SummaryTyped from "./basicInfo/SummaryTyped";
import TextInputTyped from "./basicInfo/TextInputTyped";
import { ProductData } from "../config/SampleData";
import Start from "./basicInfo/Start";
import DropdownTyped from "./basicInfo/DropdownTyped";
import { activeSegments } from "../constants";

const GenericForm = ({
  currentSubStep,
  onNext,
  onPrevious,
  questions,
  onSkipComponent,
  resetData,
}) => {
  const [formData, setFormDataState] = useState([]);
  const [questionStep, setQuestionStep] = useState(0);
  const [error, setError] = useState({});
  const [history, setHistory] = useState([]); // To track visited questions
  const getFilteredQuestions = () => {
    return formData?.filter((question) => {
      if (typeof question?.toBeShown === "function") {
        return question?.toBeShown(formData); // Pass the entire formData to the toBeShown callback
      }
      return true; // Show question if no toBeShown property
    });
  };
  const filteredQuestions = getFilteredQuestions();
  useEffect(() => {
    setFormDataState(questions);
  }, [questions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "customerSegment") {
      resetData(value);
    }
    const updatedFormData = formData?.map((item) => {
      if (item?.name === name) {
        if (type === "checkbox") {
          const currentValues = item?.answer ? item?.answer?.split(",") : [];
          if (checked) {
            currentValues?.push(value);
          } else {
            const index = currentValues?.indexOf(value);
            if (index > -1) {
              currentValues?.splice(index, 1);
            }
          }
          return { ...item, answer: currentValues.join(",") };
        } else {
          return { ...item, answer: value };
        }
      }
      return item;
    });
    setFormDataState(updatedFormData);

    // Call renderProducts if it exists for the current question
    const currentQuestion = formData.find((item) => item?.name === name);
    if (currentQuestion?.renderProducts) {
      currentQuestion?.renderProducts(
        type === "checkbox"
          ? updatedFormData
              ?.find((item) => item?.name === name)
              ?.answer?.split(",")
          : value
      );
    }
  };

  const validate = () => {
    const newError = {};
    const currentQuestion = filteredQuestions[questionStep]; // Get the current question from filteredQuestions
    if (!currentQuestion.answer && currentQuestion.type !== "summary") {
      newError[currentQuestion.name] = `This question is required.`;
    }

    setError(newError);
    return Object.keys(newError).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validate()) {
      const currentQuestion = formData[questionStep];
      const userAnswer = currentQuestion.answer;
      if (
        currentQuestion?.name === "customerSegment" &&
        !activeSegments?.includes(userAnswer)
      ) {
        alert(`${userAnswer} Segment is in progress.`);
        return;
      }
      // Add current question to history for back navigation
      setHistory((prev) => [...prev, questionStep]);

      // Filter the questions dynamically
      const filteredQuestions = getFilteredQuestions();

      // Determine the next question based on the user's answer
      let nextIndex;
      if (currentQuestion?.nextQuestion) {
        if (typeof currentQuestion?.nextQuestion === "object") {
          nextIndex = filteredQuestions?.findIndex(
            (q) => q.name === currentQuestion?.nextQuestion?.[userAnswer]
          );
        } else {
          nextIndex = filteredQuestions?.findIndex(
            (q) => q.name === currentQuestion?.nextQuestion
          );
        }
      } else {
        nextIndex = filteredQuestions.findIndex(
          (q, index) => index > questionStep
        );
      }

      if (nextIndex >= 0 && nextIndex < filteredQuestions?.length) {
        setQuestionStep(nextIndex);
      } else {
        onNext(formData); // Submit the form if no more questions
        setQuestionStep(0);
        setHistory([]); // Clear history after submission
      }
    }
  };

  const handleBack = (e) => {
    e.preventDefault();

    if (history?.length > 0) {
      // Get the current filtered question
      const currentFilteredQuestion = filteredQuestions[questionStep];

      // Remove the answer from the current filtered question
      // const updatedFormData = formData.map((item) => {
      //   if (item.name === currentFilteredQuestion.name) {
      //     return { ...item, answer: "" }; // Clear the answer
      //   }
      //   return item;
      // });

      // setFormDataState(updatedFormData);

      // Remove the last entry from history and set the previous question step
      const prevIndex = history?.[history?.length - 1];
      setHistory((prev) => prev?.slice(0, -1)); // Remove the last entry from history
      setQuestionStep(prevIndex);
    } else {
      onPrevious(formData); // Go back to the previous step
    }
  };
  const onStartNextClick = () => {
    setQuestionStep(1); // Start from the first question
    setHistory([0]);
  };
  const onStartSkipClick = (e) => {
    const currentQuestion = formData?.find((item) => item?.type === "start");
    if (currentQuestion?.renderProducts) {
      currentQuestion?.renderProducts("");
    }
    onNext(formData); // Submit the form if no more questions
    setQuestionStep(0);
    setHistory([]);
  };

  const renderQuestion = (question) => {
    onSkipComponent(question?.type);
    switch (question?.type) {
      case "start":
        return (
          <Start
            answer={question?.answer}
            question={question?.question}
            onNext={onStartNextClick}
            onPrevious={handleBack}
            onSkip={onStartSkipClick}
          />
        );
      case "radio":
        return (
          <RadioTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            info={question?.info}
            options={question?.options}
            name={question?.name}
          />
        );
      case "checkbox":
        return (
          <CheckboxTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            options={question?.options}
            name={question?.name}
            info={question?.info}
          />
        );
      case "dropdown-multi":
        return (
          <DropdownTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            options={question?.options}
            name={question?.name}
            info={question?.info}
            isMulti={true}
          />
        );
      case "dropdown-single":
        return (
          <DropdownTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            options={question?.options}
            name={question?.name}
            info={question?.info}
            isMulti={false}
          />
        );
      case "number":
        return (
          <NumberTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            name={question?.name}
            info={question?.info}
          />
        );
      case "textInput":
        return (
          <TextInputTyped
            key={question?.name}
            answer={question?.answer}
            handleChange={handleChange}
            error={error[question?.name]} // Pass specific error for this question
            question={question?.question}
            name={question?.name}
            info={question?.info}
          />
        );
      case "summary":
        return (
          <SummaryTyped
            formData={formData}
            onNext={handleNext}
            onPrevious={handleBack}
            info={question?.info}
          />
        );
      default:
        return null;
    }
  };
  return (
    <form
      className="generic_form"
      style={
        filteredQuestions[questionStep]?.type === "start"
          ? { alignSelf: "center", display: "flex", justifyContent: "center" }
          : {}
      }
    >
      {renderQuestion(filteredQuestions[questionStep])}
      <div className="next_btn mt-4 text-center">
        {filteredQuestions[questionStep]?.type !== "summary" &&
          filteredQuestions[questionStep]?.type !== "start" && (
            <>
              <button
                className="custom_back mt-4 ml-2"
                style={{ marginLeft: 10 }}
                onClick={handleBack}
              >
                Back
              </button>
              <button
                className="custom mt-4 ml-2"
                style={{ marginLeft: 10 }}
                onClick={handleNext}
              >
                {questionStep === filteredQuestions?.length - 1
                  ? "Submit"
                  : "Next"}
              </button>
            </>
          )}
      </div>
    </form>
  );
};

export default GenericForm;
